/**
 * Draws the brand name as a transparent wordmark PNG, for a brand that has no
 * logo to upload — the LogoStep uses it as the starting logo, redrawn live as
 * the name/font/colour change.
 *
 * `fitWordmarkPx` picks the biggest legible size that still fits the canvas.
 * It is PURE — it takes a `measure` callback instead of touching a canvas
 * itself — so the search is unit-tested in node; `drawWordmark` needs a real
 * `<canvas>` and is exercised only in the browser.
 */

export const WORDMARK_W = 1600;
export const WORDMARK_H = 500;
export const WORDMARK_MAX_PX = 220;
export const WORDMARK_MAX_WIDTH = 1440;

/** Never render smaller than this — a very long brand name should overflow
 *  its budget slightly rather than shrink to an unreadable wordmark. */
const WORDMARK_MIN_PX = 40;

/**
 * Largest font size ≤ `maxPx` whose `measure(px)` is ≤ `maxWidth`, walking
 * down from the cap one px at a time. Never returns below `WORDMARK_MIN_PX`,
 * even when nothing fits — the floor wins over the width budget. The floor
 * itself is clamped to `maxPx` so a caller-supplied cap below 40 (unusual,
 * but not this function's contract to reject) is still honoured — the
 * "≤ maxPx" half of the contract must hold no matter how low maxPx is.
 */
export function fitWordmarkPx(
  measure: (px: number) => number,
  maxPx: number = WORDMARK_MAX_PX,
  maxWidth: number = WORDMARK_MAX_WIDTH,
): number {
  const floor = Math.min(WORDMARK_MIN_PX, maxPx);
  for (let px = maxPx; px > floor; px--) {
    if (measure(px) <= maxWidth) return px;
  }
  return floor;
}

/**
 * Renders `text` centred on a transparent `WORDMARK_W`×`WORDMARK_H` canvas,
 * at the largest size `fitWordmarkPx` allows, filled with `color`. Returns a
 * `data:image/png` URL — the same shape `logoDataUrl` already expects.
 *
 * Font resolution never throws: `fontFamily` is tried first, then
 * `fallbackFamily`, then the system sans. A brand's uploaded font failing to
 * load (404, still downloading, typo'd name) must produce a
 * slightly-different-looking wordmark, never a broken onboarding step.
 */
export async function drawWordmark(a: {
  text: string;
  fontFamily: string;
  fallbackFamily?: string;
  color: string;
}): Promise<string> {
  if (typeof document === "undefined") {
    throw new Error("drawWordmark requires a browser (no `document`)");
  }
  const { text, color } = a;
  const family = await resolveFamily(text, a.fontFamily, a.fallbackFamily);

  const canvas = document.createElement("canvas");
  canvas.width = WORDMARK_W;
  canvas.height = WORDMARK_H;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("drawWordmark: 2D canvas context unavailable");

  const fontAt = (px: number): string => `700 ${px}px "${family}"`;
  const px = fitWordmarkPx((candidate) => {
    ctx.font = fontAt(candidate);
    return ctx.measureText(text).width;
  });

  ctx.clearRect(0, 0, WORDMARK_W, WORDMARK_H);
  ctx.font = fontAt(px);
  ctx.fillStyle = color;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(text, WORDMARK_W / 2, WORDMARK_H / 2);

  return canvas.toDataURL("image/png");
}

/**
 * `fontFamily` → `fallbackFamily` → system sans, stopping at the first one
 * `document.fonts.load` resolves for. The generic `sans-serif` at the end
 * always resolves, so this never runs out of options and never rejects.
 */
async function resolveFamily(text: string, fontFamily: string, fallbackFamily?: string): Promise<string> {
  const candidates = [fontFamily, fallbackFamily, "sans-serif"].filter((f): f is string =>
    Boolean(f && f.trim()),
  );
  for (const family of candidates) {
    try {
      await document.fonts.load(`700 ${WORDMARK_MAX_PX}px "${family}"`, text);
      return family;
    } catch {
      // A font problem must never throw here — try the next candidate.
    }
  }
  return "sans-serif";
}
